import { useState, useMemo } from 'react'
import {
  X,
  Pencil,
  Trash2,
  Check,
  Loader2,
  ChevronDown,
  ChevronRight,
  FileText,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { getLocalName } from '@/lib/ontology/namespaces'
import {
  useInstanceDetails,
  useUpdateInstanceLabel,
  useUpdateInstanceProperty,
  useRemoveInstanceProperty,
  useDeleteInstance,
} from '@/hooks/useInstances'
import { useEntityFacets } from '@/hooks/useFacetGraph'
import { LinkedFacetsList } from '@/components/sources/LinkedFacetCard'

interface InstanceDetailPanelProps {
  instanceUri: string
  color?: string
  bgColor?: string
  onClose: () => void
  onNavigate?: (uri: string) => void
  onDeleted?: () => void
  fullWidth?: boolean
}

export function InstanceDetailPanel({
  instanceUri,
  color = '#847561',
  bgColor,
  onClose,
  onNavigate,
  onDeleted,
  fullWidth = false,
}: InstanceDetailPanelProps) {
  const { data, isLoading, refetch } = useInstanceDetails(instanceUri)
  const updateLabel = useUpdateInstanceLabel()
  const updateProperty = useUpdateInstanceProperty()
  const removeProperty = useRemoveInstanceProperty()
  const deleteInstance = useDeleteInstance()

  const [editingLabel, setEditingLabel] = useState(false)
  const [labelValue, setLabelValue] = useState('')
  const [editingProp, setEditingProp] = useState<{ uri: string; value: string; index: number } | null>(null)
  const [editPropValue, setEditPropValue] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [facetsExpanded, setFacetsExpanded] = useState(true)

  // Fetch linked facets for this entity
  const { facets, count: facetCount, isLoading: facetsLoading } = useEntityFacets(instanceUri)

  const instanceLabel = useMemo(() => {
    if (!data) return getLocalName(instanceUri)
    // Try to get label from properties
    const labelProp = data.properties['http://www.w3.org/2000/01/rdf-schema#label']
    if (labelProp && labelProp.length > 0) {
      return labelProp[0].value
    }
    return getLocalName(instanceUri)
  }, [data, instanceUri])

  const handleStartEditLabel = () => {
    setLabelValue(instanceLabel)
    setEditingLabel(true)
  }

  const handleSaveLabel = async () => {
    if (labelValue.trim() && labelValue !== instanceLabel) {
      await updateLabel.mutateAsync({ instanceUri, newLabel: labelValue.trim() })
      await refetch()
    }
    setEditingLabel(false)
  }

  const handleStartEditProp = (propUri: string, value: string, index: number) => {
    setEditingProp({ uri: propUri, value, index })
    setEditPropValue(value)
  }

  const handleSaveProp = async () => {
    if (editingProp && editPropValue !== editingProp.value) {
      await updateProperty.mutateAsync({
        instanceUri,
        propertyUri: editingProp.uri,
        oldValue: editingProp.value,
        newValue: editPropValue,
      })
      await refetch()
    }
    setEditingProp(null)
  }

  const handleRemoveProp = async (propUri: string, value: string) => {
    await removeProperty.mutateAsync({ instanceUri, propertyUri: propUri, value })
    await refetch()
  }

  const handleDelete = async () => {
    await deleteInstance.mutateAsync(instanceUri)
    onDeleted?.()
    onClose()
  }

  const cardStyle = fullWidth
    ? { height: '100%', display: 'flex', flexDirection: 'column' as const }
    : { width: '350px', flexShrink: 0, display: 'flex', flexDirection: 'column' as const }

  if (isLoading) {
    return (
      <Card style={{ ...cardStyle, alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 style={{ width: '32px', height: '32px', animation: 'spin 1s linear infinite', color: 'var(--color-text-muted)' }} />
      </Card>
    )
  }

  if (!data) {
    return (
      <Card style={{ ...cardStyle, alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--color-text-muted)' }}>Instance not found</p>
      </Card>
    )
  }

  // Filter out rdfs:label from displayed properties since we show it in the header
  const displayProperties = Object.entries(data.properties).filter(
    ([uri]) => uri !== 'http://www.w3.org/2000/01/rdf-schema#label'
  )

  return (
    <Card style={{ ...cardStyle, overflow: 'hidden' }}>
      <CardHeader style={{ borderBottom: '1px solid var(--color-border)', padding: '16px', backgroundColor: bgColor }}>
        <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {editingLabel ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="text"
                  value={labelValue}
                  onChange={(e) => setLabelValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveLabel()}
                  autoFocus
                  style={{
                    flex: 1,
                    fontSize: '16px',
                    fontWeight: 600,
                    color,
                    padding: '4px 8px',
                    border: `1px solid ${color}`,
                    borderRadius: '6px',
                    outline: 'none',
                    minWidth: 0,
                  }}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSaveLabel}
                  disabled={updateLabel.isPending}
                  style={{ flexShrink: 0, width: '28px', height: '28px' }}
                >
                  <Check style={{ width: '14px', height: '14px', color: 'var(--color-success)' }} />
                </Button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CardTitle style={{ fontSize: '16px', color, wordBreak: 'break-word', flex: 1 }}>
                  {instanceLabel}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleStartEditLabel}
                  style={{ flexShrink: 0, width: '28px', height: '28px', opacity: 0.6 }}
                >
                  <Pencil style={{ width: '12px', height: '12px' }} />
                </Button>
              </div>
            )}
            {data.types && data.types.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                {data.types.map((type) => (
                  <Badge
                    key={type.uri}
                    style={{ fontSize: '10px', backgroundColor: color, color: 'white' }}
                  >
                    {type.label}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X style={{ width: '16px', height: '16px' }} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Properties */}
        {displayProperties.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: '10px' }}>
              Properties
            </h4>
            <div style={{ backgroundColor: 'var(--color-warm-50)', borderRadius: '8px', padding: '12px' }}>
              {displayProperties.map(([propUri, values]) => (
                <div key={propUri} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid var(--color-border-subtle)' }}>
                  <div style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    {getLocalName(propUri)}
                  </div>
                  {values.map((v, i) => {
                    const isEditing = editingProp?.uri === propUri && editingProp?.index === i
                    const isUri = v.value.startsWith('http://') || v.value.startsWith('https://')

                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                        {isEditing ? (
                          <>
                            <input
                              type="text"
                              value={editPropValue}
                              onChange={(e) => setEditPropValue(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleSaveProp()}
                              autoFocus
                              style={{
                                flex: 1,
                                fontSize: '13px',
                                padding: '2px 6px',
                                border: '1px solid var(--color-border)',
                                borderRadius: '4px',
                                outline: 'none',
                              }}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={handleSaveProp}
                              disabled={updateProperty.isPending}
                              style={{ width: '24px', height: '24px' }}
                            >
                              <Check style={{ width: '12px', height: '12px', color: 'var(--color-success)' }} />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span style={{ flex: 1, fontSize: '13px' }}>
                              {isUri && onNavigate ? (
                                <button
                                  onClick={() => onNavigate(v.value)}
                                  style={{ color, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' }}
                                >
                                  {v.label || getLocalName(v.value)}
                                </button>
                              ) : (
                                <span>{v.value}</span>
                              )}
                            </span>
                            {!isUri && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleStartEditProp(propUri, v.value, i)}
                                style={{ width: '24px', height: '24px', opacity: 0.5 }}
                              >
                                <Pencil style={{ width: '10px', height: '10px' }} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveProp(propUri, v.value)}
                              disabled={removeProperty.isPending}
                              style={{ width: '24px', height: '24px', opacity: 0.5 }}
                            >
                              <Trash2 style={{ width: '10px', height: '10px', color: 'var(--color-error)' }} />
                            </Button>
                          </>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Outgoing Relationships */}
        {data.outgoing.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ fontSize: '12px', fontWeight: 600, color, textTransform: 'uppercase', marginBottom: '10px' }}>
              Connected To
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {data.outgoing.map((rel, i) => (
                <div
                  key={i}
                  onClick={() => onNavigate?.(rel.target)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 12px',
                    backgroundColor: `${color}10`,
                    borderRadius: '6px',
                    cursor: onNavigate ? 'pointer' : 'default',
                    border: `1px solid ${color}30`,
                  }}
                >
                  <Badge style={{ fontSize: '9px', backgroundColor: color, color: 'white' }}>
                    {getLocalName(rel.property)}
                  </Badge>
                  <span style={{ fontSize: '12px', color, flex: 1 }}>{rel.targetLabel}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Incoming Relationships */}
        {data.incoming.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: '10px' }}>
              Referenced By
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {data.incoming.map((rel, i) => (
                <div
                  key={i}
                  onClick={() => onNavigate?.(rel.source)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 12px',
                    backgroundColor: 'var(--color-warm-50)',
                    borderRadius: '6px',
                    cursor: onNavigate ? 'pointer' : 'default',
                  }}
                >
                  <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)', flex: 1 }}>{rel.sourceLabel}</span>
                  <Badge style={{ fontSize: '9px' }} variant="outline">
                    {getLocalName(rel.property)}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Linked Facets */}
        {facetCount > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => setFacetsExpanded(!facetsExpanded)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '0',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                marginBottom: '10px',
              }}
            >
              {facetsExpanded ? (
                <ChevronDown style={{ width: '14px', height: '14px', color: '#c9a227' }} />
              ) : (
                <ChevronRight style={{ width: '14px', height: '14px', color: '#c9a227' }} />
              )}
              <FileText style={{ width: '14px', height: '14px', color: '#c9a227' }} />
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#c9a227', textTransform: 'uppercase' }}>
                Linked Facets ({facetCount})
              </span>
            </button>
            {facetsExpanded && (
              <div style={{ backgroundColor: 'var(--color-warm-50)', borderRadius: '8px', padding: '12px' }}>
                {facetsLoading ? (
                  <Loader2 style={{ width: '20px', height: '20px', animation: 'spin 1s linear infinite', margin: '0 auto', display: 'block' }} />
                ) : facets.length === 0 ? (
                  <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', textAlign: 'center' }}>
                    No linked facets
                  </p>
                ) : (
                  <LinkedFacetsList
                    facets={facets}
                    compact
                    emptyMessage="No linked facets"
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* No data message */}
        {displayProperties.length === 0 && data.outgoing.length === 0 && data.incoming.length === 0 && facetCount === 0 && (
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', textAlign: 'center', padding: '20px' }}>
            No properties, relationships, or linked facets found.
          </p>
        )}

        {/* Delete Button */}
        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--color-border-subtle)' }}>
          {showDeleteConfirm ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <p style={{ fontSize: '12px', color: 'var(--color-error)', textAlign: 'center' }}>
                Are you sure? This will delete the instance and all its relationships.
              </p>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleteInstance.isPending}
                >
                  {deleteInstance.isPending ? (
                    <Loader2 style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} />
                  ) : (
                    'Delete'
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              style={{ width: '100%', color: 'var(--color-error)', opacity: 0.7 }}
            >
              <Trash2 style={{ width: '14px', height: '14px', marginRight: '6px' }} />
              Delete Instance
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
